/**
 * AssistantPanel — slide-out chat panel for conversational queries.
 * Hotkey: Cmd+I (wired in consuming app's App.tsx).
 * Icon entry: <ChatIconButton /> in the app's topbar (gtm-intelligence NavBar).
 */

import { useEffect, useRef, useState } from "react";
import { Drawer } from "../primitives/Drawer.js";
import { ChatHeader } from "./ChatHeader.js";
import { ChatHistorySidebar } from "./ChatHistorySidebar.js";
import { ChatInput } from "./ChatInput.js";
import { ChatMessageList } from "./ChatMessageList.js";
import { usePageContext } from "./PageContextProvider.js";
import { StickyAgentBadge } from "./StickyAgentBadge.js";
import type { ChatAssistant } from "./types.js";
import { useAssistant } from "./useAssistant.js";
import { useConversations } from "./useConversations.js";

export interface AssistantPanelProps {
  open: boolean;
  onClose: () => void;
  defaultAssistant?: ChatAssistant;
  conversationId?: string | null;
  onConversationChange?: (id: string | null) => void;
  /**
   * Seed the input textarea with this text the next time the panel opens.
   * Consumed once — subsequent opens don't re-seed unless the value changes.
   */
  initialInput?: string;
}

export function AssistantPanel({
  open,
  onClose,
  defaultAssistant = "jared",
  conversationId: externalConversationId,
  onConversationChange,
  initialInput,
}: AssistantPanelProps) {
  const pageContext = usePageContext();
  const [showHistory, setShowHistory] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Seed the input when the panel opens with a non-empty initialInput. Tracked
  // by a ref so the seed only fires once per "open + new initialInput" transition
  // — typing further doesn't get overwritten.
  const lastSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (open && initialInput && initialInput !== lastSeededRef.current) {
      setInputValue(initialInput);
      lastSeededRef.current = initialInput;
    }
    if (!open) lastSeededRef.current = null;
  }, [open, initialInput]);

  const { messages, send, streaming, conversationId, currentAssistant, clearMessages } =
    useAssistant({
      defaultAssistant,
      pageContext,
      conversationId: externalConversationId,
      onConversationChange,
    });

  const {
    conversations,
    loading: convsLoading,
    refresh: refreshConvs,
  } = useConversations({
    enabled: open,
  });

  // Derive sticky from active conversation (authoritative after SSE done fires)
  const activeConversation = conversations.find((c) => c.id === conversationId);
  const stickyAssistant: ChatAssistant =
    activeConversation?.assistant ?? currentAssistant ?? defaultAssistant;

  function handleSend() {
    if (!inputValue.trim() || streaming) return;
    const text = inputValue;
    setInputValue("");
    void send(text);
  }

  function handleNewConversation() {
    clearMessages();
    onConversationChange?.(null);
    setShowHistory(false);
    refreshConvs();
  }

  function handleSelectConversation(id: string) {
    clearMessages();
    onConversationChange?.(id);
    setShowHistory(false);
  }

  const suggestedPrompts = pageContext?.suggestedPrompts;

  return (
    <Drawer open={open} onClose={onClose} width="default" title="Assistant">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Chat header */}
        <ChatHeader
          assistant={stickyAssistant}
          pageContext={pageContext}
          showHistory={showHistory}
          onToggleHistory={() => setShowHistory((v) => !v)}
          onNewConversation={handleNewConversation}
        />

        {/* Body: either history or messages */}
        {showHistory ? (
          <ChatHistorySidebar
            conversations={conversations}
            loading={convsLoading}
            activeId={conversationId}
            onSelect={handleSelectConversation}
          />
        ) : (
          <ChatMessageList
            messages={messages}
            streaming={streaming}
            suggestedPrompts={suggestedPrompts}
            onSendPrompt={(prompt) => {
              setInputValue(prompt);
            }}
          />
        )}

        {/* Sticky badge + input */}
        {!showHistory && <StickyAgentBadge assistant={stickyAssistant} />}
        {!showHistory && (
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            disabled={streaming}
          />
        )}
      </div>
    </Drawer>
  );
}
